import React, { useState, useEffect, useRef, useCallback } from 'react';

// Datos de marcas
const brands = [
  { name: 'Caterpillar', logo: 'https://www.catlifestyle.pe/static/version1753460577/frontend/Omnipro/Cat/default/images/logo.svg' },
  { name: 'Bosch', logo: 'https://logos-world.net/wp-content/uploads/2020/08/Bosch-Logo.png' },
  { name: 'Emtop', logo: 'https://cdn.brandfetch.io/idU-JL1ebz/w/209/h/87/theme/dark/logo.png?c=1bxid64Mup7aczewSAYMX&t=1751118223235' },
  { name: 'Fast', logo: 'https://www.fast.com.pe/assets/img/icons/logo.png' },
  { name: 'DongCheng', logo: 'https://global.dongcheng.com/_nuxt/img/logo.9a1cb0c.png' },
  { name: 'Total', logo: 'https://images.seeklogo.com/logo-png/37/1/total-herramientas-logo-png_seeklogo-379637.png' },
  { name: 'Prolismo', logo: 'https://prolimso.com/wp-content/uploads/2025/02/logo_prolimso.png' },
  { name: 'Sika', logo: 'https://sika.scene7.com/is/content/sika/Sika_ClaimU_pos_rgb' },
  { name: 'Daxo', logo: 'https://daxso.pe/wp-content/uploads/2023/06/logo-daxso.png' },
  { name: 'Gala', logo: 'https://cdn-lmkib.nitrocdn.com/yAveCnZiTUzABFxdcefhCkmNXRQUzHfo/assets/images/optimized/rev-01f6040/ceramicosgala.com.pe/wp-content/uploads/2024/06/ceramicos_gala_logo-1.svg' },
  { name: 'Polinplast', logo: 'https://polinplast.com.pe/wp-content/uploads/2023/05/LOGO-POLINPLAST-PRINCIPAL.png' },
];

// Hook personalizado para detectar viewport
const useViewport = (): ViewportType => {
  const [viewport, setViewport] = useState<ViewportType>(() => {
    if (typeof window !== 'undefined') {
      const width = window.innerWidth;
      if (width <= 480) return 'mobile';
      if (width <= 768) return 'tablet';
      if (width <= 1024) return 'laptop';
      if (width <= 1440) return 'desktop';
      return 'large';
    }
    return 'desktop';
  });

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width <= 480) setViewport('mobile' as ViewportType);
      else if (width <= 768) setViewport('tablet' as ViewportType);
      else if (width <= 1024) setViewport('laptop' as ViewportType);
      else if (width <= 1440) setViewport('desktop' as ViewportType);
      else setViewport('large' as ViewportType);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return viewport;
};

// Tipos para TypeScript
type ViewportType = 'mobile' | 'tablet' | 'laptop' | 'desktop' | 'large';

interface ConfigType {
  gap: string;
  padding: string;
  logoWidth: string;
  logoHeight: string;
  fadeWidth: string;
  duration: string;
  itemsToShow: number;
}

interface BrandType {
  name: string;
  logo: string;
}

// Configuración responsiva
const getConfig = (viewport: ViewportType): ConfigType => {
  const configs: Record<ViewportType, ConfigType> = {
    mobile: {
      gap: '2rem',
      padding: '1rem 0.5rem',
      logoWidth: '120px',
      logoHeight: '60px',
      fadeWidth: '20px',
      duration: '25s',
      itemsToShow: 3
    },
    tablet: {
      gap: '2.5rem',
      padding: '2rem 1rem',
      logoWidth: '140px',
      logoHeight: '70px',
      fadeWidth: '60px',
      duration: '30s',
      itemsToShow: 4
    },
    laptop: {
      gap: '3rem',
      padding: '2.5rem 1.5rem',
      logoWidth: '160px',
      logoHeight: '80px',
      fadeWidth: '80px',
      duration: '35s',
      itemsToShow: 5
    },
    desktop: {
      gap: '4rem',
      padding: '3rem 2rem',
      logoWidth: '180px',
      logoHeight: '90px',
      fadeWidth: '100px',
      duration: '40s',
      itemsToShow: 6
    },
    large: {
      gap: '5rem',
      padding: '4rem 2.5rem',
      logoWidth: '200px',
      logoHeight: '100px',
      fadeWidth: '120px',
      duration: '45s',
      itemsToShow: 7
    }
  };
  return configs[viewport] || configs.desktop;
};

// Componente de logo con carga optimizada
interface BrandLogoProps {
  brand: BrandType;
  config: ConfigType;
  style?: React.CSSProperties;
}

const BrandLogo: React.FC<BrandLogoProps> = ({ brand, config, style = {} }) => {
  const [imageState, setImageState] = useState('loading'); // loading, loaded, error
  const imgRef = useRef(null);

  useEffect(() => {
    const img = new Image();
    img.onload = () => setImageState('loaded');
    img.onerror = () => setImageState('error');
    img.src = brand.logo;
  }, [brand.logo]);

  const logoStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: config.logoWidth,
    height: config.logoHeight,
    minWidth: config.logoWidth,
    minHeight: config.logoHeight,
    opacity: imageState === 'loaded' ? '0.8' : '0.4',
    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
    filter: 'grayscale(100%) brightness(0.9)',
    flexShrink: 0,
    position: 'relative',
    background: imageState === 'loading' ? '#f8f9fa' : 'transparent',
    borderRadius: '8px',
    cursor: 'pointer',
    transform: 'scale(1)',
    ...style
  };

  const handleMouseEnter = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.currentTarget as HTMLDivElement;
    target.style.opacity = '1';
    target.style.filter = 'grayscale(0%) brightness(1)';
    target.style.transform = 'scale(1.05)';
  };

  const handleMouseLeave = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.currentTarget as HTMLDivElement;
    target.style.opacity = imageState === 'loaded' ? '0.8' : '0.4';
    target.style.filter = 'grayscale(100%) brightness(0.9)';
    target.style.transform = 'scale(1)';
  };

  return (
    <div 
      style={logoStyle}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {imageState === 'loading' && (
        <div style={{
          width: '80%',
          height: '60%',
          background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
          backgroundSize: '200px 100%',
          animation: 'shimmer 1.5s infinite',
          borderRadius: '4px'
        }} />
      )}
      
      {imageState === 'loaded' && (
        <img 
          ref={imgRef}
          src={brand.logo} 
          alt={brand.name}
          style={{
            maxWidth: '100%',
            maxHeight: '100%',
            width: 'auto',
            height: 'auto',
            objectFit: 'contain'
          }}
        />
      )}
      
      {imageState === 'error' && (
        <div style={{ 
          color: '#94a3b8', 
          fontSize: '12px', 
          textAlign: 'center',
          fontWeight: '500'
        }}>
          {brand.name}
        </div>
      )}
    </div>
  );
};

const BrandsCarousel = () => {
  const viewport = useViewport();
  const config = getConfig(viewport);
  const [isReady, setIsReady] = useState(false);
  
  useEffect(() => {
    const timer = setTimeout(() => setIsReady(true), 500);
    return () => clearTimeout(timer);
  }, []);

  // Crear múltiples copias para scroll suave
  const repeatedBrands = [...brands, ...brands, ...brands];

  const containerStyle: React.CSSProperties = {
    width: '100vw',
    marginLeft: 'calc(-50vw + 50%)',
    overflow: 'hidden',
    background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
    padding: config.padding,
    position: 'relative'
  };

  const fadeOverlayStyle = (side: 'left' | 'right'): React.CSSProperties => ({
    content: '""',
    position: 'absolute',
    top: 0,
    bottom: 0,
    width: config.fadeWidth,
    zIndex: 10,
    pointerEvents: 'none',
    ...(side === 'left' ? {
      left: 0,
      background: 'linear-gradient(90deg, #f8fafc, transparent)'
    } : {
      right: 0,
      background: 'linear-gradient(270deg, #f8fafc, transparent)'
    })
  });

  const trackStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: config.gap,
    animation: isReady ? `scroll ${config.duration} linear infinite` : 'none',
    width: 'max-content',
    willChange: 'transform'
  };

  return (
    <div style={{
      background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
      padding: '4rem 0',
      width: '100vw',
      marginLeft: 'calc(-50vw + 50%)'
    }}>
      {/* Header */}
      <div style={{
        textAlign: 'center',
        padding: '0 2rem',
        marginBottom: '3rem'
      }}>
        <h2 style={{
          fontSize: viewport === 'mobile' ? '1.75rem' : '2.5rem',
          marginBottom: '1rem',
          color: '#1e293b',
          fontWeight: '700',
          background: 'linear-gradient(135deg, #1e293b 0%, #475569 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text'
        }}>
          Marcas de Confianza
        </h2>
        <p style={{
          color: '#64748b',
          margin: '0 auto 2rem auto',
          fontSize: viewport === 'mobile' ? '0.95rem' : '1.1rem',
          maxWidth: '600px',
          lineHeight: '1.6'
        }}>
          Trabajamos con las mejores marcas del mercado para ofrecerte productos de calidad superior
        </p>
      </div>

      {/* Carousel */}
      <div style={containerStyle}>
        {/* Fade overlays */}
        <div style={fadeOverlayStyle('left')} />
        <div style={fadeOverlayStyle('right')} />
        
        <div 
          style={trackStyle}
        >
          {repeatedBrands.map((brand, index) => (
            <BrandLogo
              key={`${brand.name}-${index}`}
              brand={brand}
              config={config}
            />
          ))}
        </div>
      </div>

      {/* CSS Animations */}
      <style>{`
        @keyframes scroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(calc(-100% / 3)); }
        }
        
        @keyframes shimmer {
          0% { background-position: -200px 0; }
          100% { background-position: calc(200px + 100%) 0; }
        }
        
        * {
          box-sizing: border-box;
        }
      `}</style>
    </div>
  );
};

export default BrandsCarousel;